import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Select, 
  MenuItem, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Divider, 
  CircularProgress, 
  Alert, 
  FormControl, 
  Button,
  Tooltip,
  IconButton,
  Snackbar,
  TextField,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete, Add } from '@mui/icons-material';
import { apiService, ProjectDocument, VectorIndex, IndexDetail, JobDetail } from '../services/api';
import { useSelector, useDispatch } from 'react-redux';
import type { RootState } from '../store/store';
import { setCurrentIndex } from '../store/projectSlice';
import styles from './TrainingDataPage.module.css';
import DeleteDocumentDialog from '../components/Chat/DeleteDocumentDialog';
import JobSubmittedDialog from '../components/Chat/JobSubmittedDialog';

const TrainingDataPage: React.FC = () => {
  const [indexes, setIndexes] = useState<VectorIndex[]>([]);
  const [detail, setDetail] = useState<IndexDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [indexLoading, setIndexLoading] = useState(false);
  const [indexError, setIndexError] = useState<string | null>(null);
  
  // State for all project documents and available documents
  const [allProjectDocuments, setAllProjectDocuments] = useState<ProjectDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  
  // State for delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<ProjectDocument | null>(null);
  const [isDeletingDocumentId, setIsDeletingDocumentId] = useState<string | null>(null);
  
  // State for add to index operation
  const [isAddingDocumentId, setIsAddingDocumentId] = useState<string | null>(null);

  // State for deleting available document
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [documentToDeleteAll, setDocumentToDeleteAll] = useState<ProjectDocument | null>(null);
  const [docIndexes, setDocIndexes] = useState<string[]>([]);
  const [loadingDocDetail, setLoadingDocDetail] = useState(false);
  const [isDeletingAllId, setIsDeletingAllId] = useState<string | null>(null);
  
  // State for job submission dialog
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [submittedJobId, setSubmittedJobId] = useState<string>('');
  const [submittedDocumentName, setSubmittedDocumentName] = useState<string>('');
  
  // State for uploading
  const [uploading, setUploading] = useState(false);
  const [notification, setNotification] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  
  // State for creating new index
  const [newIndexName, setNewIndexName] = useState('');
  const [showAddedIndicator, setShowAddedIndicator] = useState(false);

  const dispatch = useDispatch();

  // Get current project and index from Redux state
  const selectedProject = useSelector((state: RootState) => state.projects.selectedProject);
  const currentIndex = useSelector((state: RootState) => state.projects.currentIndex);

  // Get current index metadata from indexes array
  const currentIndexMetadata = useMemo(() => {
    if (!currentIndex || !indexes.length) return null;
    return indexes.find(index => index.name === currentIndex) || null;
  }, [currentIndex, indexes]);

  // Helper function to get active job for a document
  const getActiveJob = (documentId: string): JobDetail | null => {
    if (!detail?.jobs) return null;
    return detail.jobs.find(job => 
      job.document_id === documentId && 
      ['pending', 'processing', 'queued', 'in_progress'].includes(job.status.toLowerCase())
    ) || null;
  };

  // Fetch indexes
  const fetchIndexes = () => {
    console.log('TrainingDataPage - fetchIndexes called', { selectedProject });
    setIndexLoading(true);
    setIndexError(null);
    apiService.rag.listIndexes()
      .then((indexObjs: VectorIndex[]) => {
        console.log('TrainingDataPage - indices received:', indexObjs);
        setIndexes(indexObjs);
        setIndexLoading(false);
      })
      .catch((error: Error) => {
        console.error('TrainingDataPage - error fetching indices:', error);
        setIndexError('Failed to load indexes.');
        setIndexLoading(false);
      });
  };
  
  // Fetch current index details
  const fetchIndexDetail = (indexName: string) => {
    if (!indexName) return;
    setLoading(true);
    setError(null);
    apiService.rag.getIndexDetail(indexName)
      .then((data: IndexDetail) => {
        setDetail(data);
        setLoading(false);
      })
      .catch((error: Error) => {
        setError('Failed to load index details.');
        setLoading(false);
      });
  };
  
  // Comprehensive refresh function that updates everything
  const refreshAll = () => {
    fetchIndexes();
    fetchAllDocuments();
    if (currentIndex) {
      fetchIndexDetail(currentIndex);
    }
  };

  useEffect(() => {
    fetchIndexes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch indexes when selectedProject changes and is a RAG project
  useEffect(() => {
    console.log('TrainingDataPage - useEffect for selectedProject triggering', { 
      projectExists: Boolean(selectedProject), 
      projectType: selectedProject?.type
    });
    
    if (selectedProject && selectedProject.type === 'rag') {
      console.log('TrainingDataPage - project is RAG type, fetching indexes');
      fetchIndexes();
    } else {
      console.log('TrainingDataPage - project not RAG type, skipping index fetch');
    }
  }, [selectedProject]);

  useEffect(() => {
    if (!selectedProject) return;
    // Only update if RAG project and indexName is present
    if (selectedProject.type === 'rag' && selectedProject.indexName && indexes.length > 0) {
      const found = indexes.find(idx => idx.name === selectedProject.indexName);
      dispatch(setCurrentIndex(found ? found.name : indexes[0].name));
    }
    // Optionally, if not a RAG project, do not change currentIndex
  }, [selectedProject, indexes, dispatch]);

  // Fetch index detail when currentIndex changes
  // Function to fetch all project documents
  const fetchAllDocuments = () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    apiService.rag.listProjectDocuments()
      .then((data: ProjectDocument[]) => {
        console.log('TrainingDataPage - all documents received:', data);
        setAllProjectDocuments(data);
        setDocumentsLoading(false);
      })
      .catch((error: Error) => {
        console.error('TrainingDataPage - error fetching documents:', error);
        setDocumentsError('Failed to load all documents.');
        setDocumentsLoading(false);
      });
  };
  
  useEffect(() => {
    if (!currentIndex) return;
    let isMounted = true;
    setLoading(true);
    setError(null);
    apiService.rag.getIndexDetail(currentIndex)
      .then((data: IndexDetail) => {
        if (isMounted) {
          setDetail(data);
          setLoading(false);
        }
      })
      .catch((error: Error) => {
        if (isMounted) {
          setError('Failed to load index details.');
          setLoading(false);
        }
      });
    // Also refresh the tenant-wide documents list on index change
    fetchAllDocuments();
    return () => { isMounted = false; };
  }, [currentIndex]);
  
  // Fetch all documents when selectedProject changes
  useEffect(() => {
    if (selectedProject) {
      fetchAllDocuments();
    }
  }, [selectedProject]);
  
  // Sort documents by upload_date/modified_date in descending order
  const sortDocumentsByDate = (documents: any[]) => {
    return [...documents].sort((a, b) => {
      const dateA = a.last_modified || a.upload_date || a.created_at || '';
      const dateB = b.last_modified || b.upload_date || b.created_at || '';
      
      // Sort in descending order (newest first)
      return dateB.localeCompare(dateA);
    });
  };
  
  // Filter available documents (not in the current index) and sort by date
  const availableDocuments = useMemo(() => {
    if (!currentIndex || !allProjectDocuments.length || !detail?.documents) {
      return [];
    }
    
    const indexedDocIds = new Set(detail.documents.map((doc: any) => doc.id));
    
    const filteredDocs = allProjectDocuments.filter(doc => {
      // Check if document is not in the current index
      const notInCurrentIndex = !doc.indexes?.includes(currentIndex) && !indexedDocIds.has(doc.id);
      return notInCurrentIndex;
    });
    
    // Sort by date descending
    return sortDocumentsByDate(filteredDocs);
  }, [allProjectDocuments, currentIndex, detail?.documents]);

  // Add date formatting function
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  const handleDeleteDocument = (document: ProjectDocument) => {
    setIsDeletingDocumentId(document.id); // Disable icon immediately
    setDocumentToDelete(document);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!documentToDelete || !currentIndex) {
      console.error('Missing document or index for deletion');
      return;
    }

    try {
      await apiService.rag.deleteDocumentFromIndex(documentToDelete.id, currentIndex);
      console.log(`Successfully deleted document ${documentToDelete.id} from index ${currentIndex}`);
      
      // Close dialog - don't clear isDeletingDocumentId, row will disappear
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      
      // Add 4-second delay before refreshing to allow API changes to propagate
      setTimeout(async () => {
        await fetchIndexDetail(currentIndex);
        await fetchAllDocuments();
        setIsDeletingDocumentId(null); // Clear deletion state after refresh
      }, 3000);
      
    } catch (error) {
      console.error('Error deleting document:', error);
      setError('Failed to delete document from index.');
      setIsDeletingDocumentId(null); // Re-enable on error
    }
  };

  const handleViewDocument = async (documentId: string) => {
    try {
      const { blob, filename } = await apiService.rag.downloadDocument(documentId);
      const url = URL.createObjectURL(blob);
      // Open in a new tab; browser will handle PDF/text rendering
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      if (!win) {
        // Fallback: create an anchor and click
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        if (filename) a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      // Revoke later to allow the new tab to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      console.error('Failed to open document:', e);
      setNotification({ message: e?.message || 'Failed to open document', severity: 'error' });
    }
  };

  const handleDeleteAvailable = async (document: ProjectDocument) => {
    try {
      setDocumentToDeleteAll(document);
      setConfirmDeleteOpen(true);
      setLoadingDocDetail(true);
      const detail = await apiService.rag.getDocument(document.id);
      const indexes = Array.isArray(detail?.indexes)
        ? (detail.indexes as string[])
        : Array.isArray(detail?.index_names)
          ? (detail.index_names as string[])
          : [];
      setDocIndexes(indexes);
    } catch (e) {
      console.error('Failed to fetch document detail:', e);
      setDocIndexes([]);
    } finally {
      setLoadingDocDetail(false);
    }
  };

  const confirmDeleteDocumentAll = async () => {
    if (!documentToDeleteAll) return;
    // Do not delete if document is in any indexes
    if (docIndexes.length > 0) {
      setConfirmDeleteOpen(false);
      return;
    }
    try {
      setIsDeletingAllId(documentToDeleteAll.id);
      await apiService.rag.deleteDocument(documentToDeleteAll.id);
      setNotification({ message: `Deleted document: ${documentToDeleteAll.filename}`, severity: 'success' });
      setConfirmDeleteOpen(false);
      setDocumentToDeleteAll(null);
      setDocIndexes([]);
      await fetchAllDocuments();
      if (currentIndex) await fetchIndexDetail(currentIndex);
    } catch (e: any) {
      console.error('Delete document failed:', e);
      setNotification({ message: e?.message || 'Failed to delete document', severity: 'error' });
    } finally {
      setIsDeletingAllId(null);
    }
  };

  const handleAddToIndex = async (document: ProjectDocument) => {
    if (!currentIndex) {
      console.error('No current index selected');
      setError('Please select an index first.');
      return;
    }

    // Disable icon immediately
    setIsAddingDocumentId(document.id);

    try {
      // Determine if this is scraped content or a regular document based on source
      // source: 'upload' = document (use document indexing endpoint)
      // source: 'crawl' = JSON content (use content indexing endpoint)
      const isScrapedContent = document.source_type === 'crawl';
      
      let response;
      if (isScrapedContent) {
        console.log(`Adding scraped content ${document.id} to index ${currentIndex}`);
        response = await apiService.rag.addContentToIndex(document.id, currentIndex);
      } else {
        console.log(`Adding document ${document.id} to index ${currentIndex}`);
        response = await apiService.rag.addDocumentToIndex(document.id, currentIndex);
      }

      // Show the job submission dialog
      setSubmittedJobId(response.job_id);
      setSubmittedDocumentName(document.filename);
      setJobDialogOpen(true);
      
    } catch (error) {
      console.error('Error adding document to index:', error);
      setError('Failed to add document to index.');
      setIsAddingDocumentId(null); // Re-enable on error
    }
  };

  const handleJobDialogClose = async () => {
    setJobDialogOpen(false);
    setSubmittedJobId('');
    setSubmittedDocumentName('');
    
    // Add 5-second delay before refreshing to allow API changes to propagate
    setTimeout(async () => {
      await fetchIndexDetail(currentIndex);
      await fetchAllDocuments();
      setIsAddingDocumentId(null); // Clear adding state after refresh
    }, 5000);
  };

  const handleCrawlFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    console.log('Selected crawl file:', file);
    
    // Reset the input so the same file can be selected again
    event.target.value = '';
    
    try {
      setUploading(true);
      const response = await apiService.rag.uploadCrawlContent(file, file.name);
      console.log('Crawl content upload successful:', response);
      
      // Show success message
      setNotification({
        message: `Successfully uploaded crawl content: ${file.name}`,
        severity: 'success'
      });
      
      // Refresh document lists after a delay
      setTimeout(async () => {
        if (currentIndex) {
          await fetchIndexDetail(currentIndex);
        }
        await fetchAllDocuments();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error uploading crawl content:', error);
      setNotification({
        message: `Failed to upload crawl content: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  // Handle creating new index
  const handleCreateIndex = async () => {
    if (!newIndexName.trim()) {
      setNotification({ message: 'Please enter an index name', severity: 'error' });
      return;
    }
    
    try {
      setIndexLoading(true);
      await apiService.rag.createIndex(newIndexName.trim());
      setNotification({ message: `Index "${newIndexName}" created successfully!`, severity: 'success' });
      setNewIndexName('');
      // Show the "added" indicator
      setShowAddedIndicator(true);
      setTimeout(() => setShowAddedIndicator(false), 1500);
      // Refresh the indexes list to show the new index
      fetchIndexes();
    } catch (error: any) {
      console.error('Error creating index:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error || {}));
      
      let errorMessage = 'Failed to create index. Please try again.';
      
      // Handle different error structures
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.details) {
        errorMessage = error.data.details;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Special handling for 409 conflict (duplicate index)
      if (error?.status === 409) {
        errorMessage = `Index "${newIndexName}" already exists. Please choose a different name.`;
      }
      
      setNotification({ 
        message: errorMessage, 
        severity: 'error' 
      });
    } finally {
      setIndexLoading(false);
    }
  };

  const handleDocumentFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    console.log('Selected document file:', file);
    
    // Reset the input so the same file can be selected again
    event.target.value = '';
    
    try {
      setUploading(true);
      const response = await apiService.rag.uploadDocument(file, file.name);
      console.log('Document upload successful:', response);
      
      // Show success message
      setNotification({
        message: `Successfully uploaded document: ${file.name}`,
        severity: 'success'
      });
      
      // Refresh document lists after a delay
      setTimeout(async () => {
        if (currentIndex) {
          await fetchIndexDetail(currentIndex);
        }
        await fetchAllDocuments();
      }, 2000);
      
    } catch (error: any) {
      console.error('Error uploading document:', error);
      setNotification({
        message: `Failed to upload document: ${error.message || 'Unknown error'}`,
        severity: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box className={styles.container}>
      <Typography variant="h4" className={styles.pageTitle}>Training Data</Typography>
      
      <Paper className={styles.section}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Vector Database Index</Typography>
          
          {indexLoading ? (
            <CircularProgress size={28} />
          ) : indexError ? (
            <Alert severity="error" sx={{ mb: 2 }}>{indexError}</Alert>
          ) : (
            <FormControl fullWidth margin="normal" sx={{ position: 'relative', maxWidth: 400 }}>
              <Select
                value={indexes.find(idx => idx.name === currentIndex) ? currentIndex : ''}
                onChange={(e) => dispatch(setCurrentIndex(e.target.value as string))}
                displayEmpty
                renderValue={(value) => {
                  if (!value) return <em>Select an index</em>;
                  return <strong>{value}</strong>;
                }}
                sx={{ fontWeight: 500, '.MuiSelect-select': { display: 'flex', alignItems: 'center' } }}
                MenuProps={{
                  PaperProps: {
                    style: { maxHeight: 300 },
                  },
                }}
                endAdornment={
                  <Box sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
                    <Button
                      variant="text"
                      size="small"
                      onClick={refreshAll}
                      disabled={indexLoading || documentsLoading || loading}
                      sx={{ color: '#1976d2', fontWeight: 500, mr: 2, minWidth: 'auto', px: 0.5 }}
                    >
                      Refresh
                    </Button>
                  </Box>
                }
              >
                {indexes.map(idx => (
                  <MenuItem key={idx.name} value={idx.name}>{idx.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Add new index section */}
        <Box sx={{ mt: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Create New Index</Typography>
          <Box sx={{ display: 'flex', gap: 1, maxWidth: 400 }}>
            <TextField
              size="small"
              placeholder="Enter new index name"
              value={newIndexName}
              onChange={(e) => setNewIndexName(e.target.value)}
              sx={{ flex: 1 }}
              disabled={indexLoading}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={handleCreateIndex}
              disabled={!newIndexName.trim() || indexLoading}
            >
              ADD
            </Button>
            <Fade in={showAddedIndicator}>
              <Typography variant="caption" sx={{ color: 'success.main', ml: 1, alignSelf: 'center' }}>
                Added
              </Typography>
            </Fade>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : detail ? (
          <>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Index Details</Typography>
              <TableContainer component={Paper} sx={{ maxWidth: 600 }} variant="outlined">
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell><strong>Name</strong></TableCell>
                      <TableCell>{currentIndex}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Document Count</strong></TableCell>
                      <TableCell>{detail.documents?.length || 0}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Created At</strong></TableCell>
                      <TableCell>{currentIndexMetadata?.created_at || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Embedding Model</strong></TableCell>
                      <TableCell>{currentIndexMetadata?.embedding_model || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Embedding Dimension</strong></TableCell>
                      <TableCell>{currentIndexMetadata?.embedding_dimension || 'N/A'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell><strong>Similarity</strong></TableCell>
                      <TableCell>{currentIndexMetadata?.similarity || 'N/A'}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>Indexed Documents</Typography>
              <TableContainer component={Paper} sx={{ maxWidth: '100%', maxHeight: 400, overflow: 'auto' }} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Filename</TableCell>
                      <TableCell>Content Type</TableCell>
                      <TableCell>Size</TableCell>
                      <TableCell>Upload Date</TableCell>
                      <TableCell>Last Modified</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.documents && sortDocumentsByDate(detail.documents).map((doc: any) => (
                      <TableRow key={doc.id}>
                        <TableCell>
                          <Tooltip title={doc.filename} placement="top">
                            <span
                              onClick={() => handleViewDocument(doc.id)}
                              style={{ display: 'block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                            >
                              {doc.filename}
                            </span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{doc.content_type}</TableCell>
                        <TableCell>{doc.size ? doc.size.toLocaleString() : ''}</TableCell>
                        <TableCell>{formatDate(doc.upload_date)}</TableCell>
                        <TableCell>{formatDate(doc.last_modified)}</TableCell>
                        <TableCell>{doc.status}</TableCell>
                        <TableCell>{doc.source_type}</TableCell>
                        <TableCell>
                          <IconButton 
                            onClick={() => handleDeleteDocument(doc)}
                            disabled={deleteDialogOpen && documentToDelete?.id === doc.id || isDeletingDocumentId === doc.id}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {!detail.documents || detail.documents.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">No documents in this index</TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
            
            {/* Available Documents Section */}
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Available Documents</Typography>
              
              {documentsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : documentsError ? (
                <Alert severity="error" sx={{ mb: 2 }}>{documentsError}</Alert>
              ) : (
                <TableContainer component={Paper} sx={{ maxWidth: '100%', maxHeight: 400, overflow: 'auto' }} variant="outlined">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Filename</TableCell>
                        <TableCell>Content Type</TableCell>
                        <TableCell>Size</TableCell>
                        <TableCell>Upload Date</TableCell>
                        <TableCell>Last Modified</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Source</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {availableDocuments.map((doc: ProjectDocument) => {
                        const activeJob = getActiveJob(doc.id);
                        const jobStatus = activeJob ? 'processing' : '';
                        
                        return (
                          <TableRow key={doc.id}>
                            <TableCell>
                              <Tooltip title={doc.filename} placement="top">
                                <span
                                  onClick={() => handleViewDocument(doc.id)}
                                  style={{ display: 'block', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer', color: '#1976d2', textDecoration: 'underline' }}
                                >
                                  {doc.filename}
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell>{doc.content_type}</TableCell>
                            <TableCell>{doc.size ? doc.size.toLocaleString() : ''}</TableCell>
                            <TableCell>{formatDate(doc.upload_date)}</TableCell>
                            <TableCell>{formatDate(doc.last_modified)}</TableCell>
                            <TableCell>
                              {jobStatus ? (
                                <span style={{ 
                                  color: '#1976d2', 
                                  fontWeight: 'bold',
                                  backgroundColor: '#e3f2fd',
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '0.75rem',
                                  display: 'inline-block',
                                  textAlign: 'center'
                                }}>
                                  {jobStatus}
                                </span>
                              ) : (
                                doc.status || 'available'
                              )}
                            </TableCell>
                            <TableCell>{doc.source_type}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                {activeJob ? (
                                  <Tooltip title="Cannot add to index while processing">
                                    <span>
                                      <IconButton disabled>
                                        <Add />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <IconButton onClick={() => handleAddToIndex(doc)} disabled={isAddingDocumentId === doc.id}>
                                    <Add />
                                  </IconButton>
                                )}
                                <Tooltip title={activeJob ? 'Cannot delete while processing' : 'Delete document (removes from all indexes)'}>
                                  <span>
                                    <IconButton onClick={() => handleDeleteAvailable(doc)} disabled={!!activeJob || isDeletingAllId === doc.id}>
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {availableDocuments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">No additional documents available</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
            
            {/* Upload Buttons Section */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              {/* Hidden file inputs */}
              <input
                type="file"
                accept=".json"
                style={{ display: 'none' }}
                id="crawl-upload-input"
                onChange={handleCrawlFileChange}
              />
              <input
                type="file"
                accept=".txt,.pdf"
                style={{ display: 'none' }}
                id="document-upload-input"
                onChange={handleDocumentFileChange}
              />
              
              {/* Upload buttons */}
              <label htmlFor="crawl-upload-input">
                <Button
                  component="span"
                  variant="outlined"
                  color="primary"
                >
                  Upload Crawl
                </Button>
              </label>
              <label htmlFor="document-upload-input">
                <Button
                  component="span"
                  variant="outlined"
                  color="primary"
                >
                  Upload Document
                </Button>
              </label>
            </Box>
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" color="text.secondary">
              Select an index to view details
            </Typography>
          </Box>
        )}
      </Paper>
      {deleteDialogOpen && documentToDelete && (
        <DeleteDocumentDialog
          open={deleteDialogOpen}
          onClose={() => {
            setDeleteDialogOpen(false);
            setDocumentToDelete(null);
            setIsDeletingDocumentId(null); // Re-enable icon on cancel
          }}
          onConfirm={handleConfirmDelete}
          document={documentToDelete}
        />
      )}
      {jobDialogOpen && (
        <JobSubmittedDialog
          open={jobDialogOpen}
          onClose={async () => {
            setJobDialogOpen(false);
            setSubmittedJobId('');
            setSubmittedDocumentName('');
            
            // Add 5-second delay before refreshing to allow API changes to propagate
            setTimeout(async () => {
              await fetchIndexDetail(currentIndex);
              await fetchAllDocuments();
              setIsAddingDocumentId(null); // Clear adding state after refresh
            }, 5000);
          }}
          jobId={submittedJobId}
          documentName={submittedDocumentName}
        />
      )}
      {/* Delete confirm dialog for Available Documents */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Document</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            {docIndexes.length > 0
              ? 'This document is still indexed and cannot be deleted.'
              : 'Deleting this document will remove it from the server. This action cannot be undone.'}
          </Typography>
          {loadingDocDetail ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading document details…</Typography>
            </Box>
          ) : docIndexes.length > 0 ? (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Currently in indexes:</Typography>
              <ul style={{ marginTop: 4 }}>
                {docIndexes.map((name) => (
                  <li key={name}><Typography variant="body2">{name}</Typography></li>
                ))}
              </ul>
            </>
          ) : (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>This document is not currently in any index.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          {docIndexes.length > 0 ? (
            <Button onClick={() => setConfirmDeleteOpen(false)}>OK</Button>
          ) : (
            <>
              <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
              <Button color="error" onClick={confirmDeleteDocumentAll} disabled={isDeletingAllId != null}>
                {isDeletingAllId ? 'Deleting…' : 'Delete'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notification}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setNotification(null)} 
          severity={notification?.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TrainingDataPage;
